import { useEffect, useCallback } from 'react';
import { RadioGroup } from '@headlessui/react';
import {
  ShippingMethod,
  V2ShippingMethod,
} from '../../interface/checkoutInterface';
import { getCurrencyWithSymbol } from '../../utils/formatPrice';
import LoadingSpinner from '../shared/LoadingSpinner';
import {
  setApiLoading,
  setSelectedShippingMethod,
} from '../../redux/slices/checkoutSlice';
import { ComponentConfig } from '@measured/puck';
import {
  MarginProps,
  marginFields,
} from '../../../page-builder/puck/reusable-props/margin';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../services/constants';
import CustomTooltip from '../../../components/common/tooltip';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getNewMarginStyles,
  getPaddingStyles,
  getQuillEditorText,
} from '../../utils/helper';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import checkoutService from '../../api/checkoutService';
import { setCartData } from '../../redux/slices/cartSlice';
import { getIsEdit } from '../../utils/constants';
import { CustomizeProps } from '../../interface/commonInterface';
import TextCustomInput from '../../puck/reusable-props/TextCustomInput';
import {
  paddingFields,
  paddingProps,
  PaddingProps,
} from '../../puck/reusable-props/padding';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';
import ColorPicker from '../../puck/reusable-props/ColorPicker';
import SelectedOptionCustomization, {
  ISelectedOptionCustomizationProps,
} from '../address/billingFormFields/SelectedOptionCustomization';
import DisplayAfterShippingField, {
  IDisplayAfterShippingFormIsFilled,
} from './DisplayAfterShippingField';
import { ShippingMethodLoader } from './ShippingMethodLoader';

export type ShippingMethodSelectProps = showComponentProps &
  MarginProps &
  PaddingProps & {
    background: {
      color: string;
      isInherit: boolean;
    };
    selectedOptionCustomization: ISelectedOptionCustomizationProps;
    radioColor: string;
    radioSize?: string;
    title: CustomizeProps;
    description: CustomizeProps;
    price: CustomizeProps;
    border: IBorderValueProps;
    isShowFreeForZero: boolean;
    displayAfterShippingFormIsFilled: IDisplayAfterShippingFormIsFilled;
  };
function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export const ShippingMethodSelect = ({
  useAppDispatch,
  useAppSelector,
}: ReduxDispatchProps &
  ReduxSelectorProps): ComponentConfig<ShippingMethodSelectProps> => {
  return {
    label: (
      <CustomTooltip title="ShippingMethodSelect">
        <span>Shipping Method Select</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      ...backgroungFields,
      title: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <TextCustomInput
            value={value}
            onChange={onChange}
            title="Title"
            isText={false}
            showComponent={false}
            showHover={false}
            showDeviceType={false}
            isFontFamily={true}
            isLineHeight={true}
            isLetterSpace={true}
            showTextAlign={true}
          />
        ),
      },
      radioColor: {
        type: 'custom',
        label: 'Selected radio color',
        render: ({ value, onChange, field }) => (
          <ColorPicker
            value={value}
            onChange={onChange}
            label={field.label}
            tooltip={field.label}
          />
        ),
      },
      radioSize: {
        type: 'custom',
        label: 'Radio Size',
        render: ({ value, onChange }) => (
          <>
            <label>Radio Size</label>
            <input
              type="Number"
              value={value || ''}
              onChange={newValue => {
                onChange(newValue.target.value);
              }}
            />
          </>
        ),
      },
      selectedOptionCustomization: {
        type: 'custom',
        render: ({ value, onChange }) => {
          return (
            <SelectedOptionCustomization value={value} onChange={onChange} />
          );
        },
      },
      description: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <TextCustomInput
            value={value}
            onChange={onChange}
            title="Description"
            isText={false}
            showComponent={false}
            showHover={false}
            showDeviceType={false}
            isFontFamily={true}
            isLineHeight={true}
            isLetterSpace={true}
            showTextAlign={true}
          />
        ),
      },
      price: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <TextCustomInput
            value={value}
            onChange={onChange}
            title="Price"
            isText={false}
            showComponent={false}
            showHover={false}
            showDeviceType={false}
            isFontFamily={true}
            isLineHeight={true}
            isLetterSpace={true}
          />
        ),
      },
      isShowFreeForZero: {
        type: 'radio',
        label: 'Show Free for Zero',
        options: [
          {
            label: 'Yes',
            value: true,
          },
          {
            label: 'No',
            value: false,
          },
        ],
      },
      displayAfterShippingFormIsFilled: {
        type: 'custom',
        label: 'Display After Shipping Form Is Filled',
        render: ({ value, onChange }) => (
          <DisplayAfterShippingField onChange={onChange} value={value} />
        ),
      },
    },
    defaultProps: {
      title: {
        deviceType: 'default',
        text: 'Shipping Method',
        weight: {
          default: 'font-bold',
          desktop: 'font-bold',
          tablet: 'font-bold',
          mobile: 'font-bold',
        },
        textColor: {
          default: '#000000',
          desktop: '#000000',
          tablet: '#000000',
          mobile: '#000000',
        },
        fontSize: {
          default: 'text-base',
          desktop: 'text-base',
          tablet: 'text-base',
          mobile: 'text-base',
        },
        textAlign: {
          default: 'text-left',
          desktop: 'text-left',
          tablet: 'text-left',
          mobile: 'text-left',
        },
        fontFamily: 'inherit-global',
        lineHeight: '',
        letterSpace: '0',
      },
      selectedOptionCustomization: {
        background: {
          color: '#FFFFFF',
          isInherit: true,
        },
        borderColor: '#ccc',
      },
      radioColor: '#4f46e5',
      radioSize: '16',
      description: {
        deviceType: 'default',
        text: 'Shipping Method',
        weight: {
          default: 'font-bold',
          desktop: 'font-bold',
          tablet: 'font-bold',
          mobile: 'font-bold',
        },
        textColor: {
          default: '#6B7280',
          desktop: '#000000',
          tablet: '#000000',
          mobile: '#000000',
        },
        fontSize: {
          default: 'text-sm',
          desktop: 'text-base',
          tablet: 'text-base',
          mobile: 'text-base',
        },
        textAlign: {
          default: 'text-left',
          desktop: 'text-left',
          tablet: 'text-left',
          mobile: 'text-left',
        },
        fontFamily: 'inherit-global',
        lineHeight: '',
        letterSpace: '0',
      },
      price: {
        deviceType: 'default',
        text: 'Shipping Method',
        weight: {
          default: 'font-medium',
          desktop: 'font-bold',
          tablet: 'font-bold',
          mobile: 'font-bold',
        },
        textColor: {
          default: '#111827',
          desktop: '#000000',
          tablet: '#000000',
          mobile: '#000000',
        },
        fontSize: {
          default: 'text-sm',
          desktop: 'text-base',
          tablet: 'text-base',
          mobile: 'text-base',
        },
        textAlign: {
          default: 'text-left',
          desktop: 'text-left',
          tablet: 'text-left',
          mobile: 'text-left',
        },
        fontFamily: 'inherit-global',
        lineHeight: '',
        letterSpace: '0',
      },
      background: {
        color: '#ffffff',
        isInherit: false,
      },
      showComponent: ['desktop', 'tablet', 'mobile'],
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
      ...paddingProps,
      ...borderProps,
      isShowFreeForZero: true,
      displayAfterShippingFormIsFilled: {
        isDisplayAfterShippingFormIsFilled: false,
        text: '<p class="ql-align-center" style="line-height: 1.75;"><sub style="font-size: 13px; font-weight: 600; color: rgb(125, 120, 120);">Enter your shipping address to view available shipping methods</sub></p>',
        background: {
          color: '#f6f6f6',
          isInherit: false,
        },
        margin: {
          deviceType: 'desktop',
          marginBottom: {
            desktop: '0',
            mobile: '0',
            tablet: '0',
          },
          marginLeft: {
            desktop: '0',
            mobile: '0',
            tablet: '0',
          },
          marginRight: {
            desktop: '0',
            mobile: '0',
            tablet: '0',
          },
          marginTop: {
            desktop: '1',
            mobile: '0',
            tablet: '0',
          },
        },
        padding: {
          deviceType: 'desktop',
          paddingBottom: {
            desktop: '10',
            mobile: '10',
            tablet: '10',
          },
          paddingLeft: {
            desktop: '10',
            mobile: '10',
            tablet: '10',
          },
          paddingRight: {
            desktop: '10',
            mobile: '10',
            tablet: '10',
          },
          paddingTop: {
            desktop: '10',
            mobile: '10',
            tablet: '10',
          },
        },
        border: {
          deviceType: 'mobile',
          borderBottomLeft: {
            desktop: '10',
            tablet: '10',
            mobile: '10',
          },
          borderBottomRight: {
            desktop: '10',
            tablet: '10',
            mobile: '10',
          },
          borderTopLeft: {
            desktop: '10',
            tablet: '10',
            mobile: '10',
          },
          borderTopRight: {
            desktop: '10',
            tablet: '10',
            mobile: '10',
          },
          borderTopWidth: {
            desktop: 0,
            tablet: 0,
            mobile: 0,
          },
          borderRightWidth: {
            desktop: 0,
            tablet: 0,
            mobile: 0,
          },
          borderBottomWidth: {
            desktop: 0,
            tablet: 0,
            mobile: 0,
          },
          borderLeftWidth: {
            desktop: 0,
            tablet: 0,
            mobile: 0,
          },
          borderWidth: {
            desktop: 1,
            tablet: 10,
            mobile: 1,
          },
          borderStyle: {
            desktop: 'border-solid',
            tablet: 'border-solid',
            mobile: 'border-solid',
          },
          borderColor: {
            desktop: '#f6f6f6',
            tablet: '#f6f6f6',
            mobile: '#f6f6f6',
          },
        },
      },
    },
    render: ({
      margin,
      showComponent,
      title,
      description,
      price,
      background,
      editMode,
      padding,
      radioColor,
      radioSize,
      border,
      selectedOptionCustomization,
      isShowFreeForZero,
      displayAfterShippingFormIsFilled,
    }) => {
      const dispatch = useAppDispatch();
      const {
        availableShippingMethods,
        selectedShippingMethod,
        isApiLoading,
        pageId,
      } = useAppSelector(state => state.checkout);
      const { cartData } = useAppSelector(state => state.cart);
      const storeData = useAppSelector(state => state.store.storeData);
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const marginStylesForLoading = getNewMarginStyles(
        displayAfterShippingFormIsFilled?.margin,
        currentDeviceType,
      );
      const isApiCartLoading = useAppSelector(
        state => state.cart.isCartApiLoading,
      );
      const state = useAppSelector(state => state);
      const isValidShippingForm = useAppSelector(
        state => state.common.isValidShippingForm,
      );
      const newPrice = {
        ...price,
        text: getQuillEditorText(price?.text, state),
      };
      const newDescription = {
        ...description,
        text: getQuillEditorText(description?.text, state),
      };
      const newTitle = {
        ...title,
        text: getQuillEditorText(title?.text, state),
      };

      const setShippingMethod = useCallback(
        async (shippingMethod: ShippingMethod) => {
          dispatch(setApiLoading(true));
          const payload = {
            ID: cartData?.ID || '',
            ShopID: storeData?.ShopID || '',
            ShippingMethodID: shippingMethod.ID,
            StoreID: Number(storeData?.StoreID),
            PageID: String(pageId) || '',
          };
          try {
            const res = await checkoutService.setShippingMethod(
              payload,
              cartData || undefined,
            );
            if (res?.data?.Cart) dispatch(setCartData(res.data.Cart));
          } catch (error) {
            // setCheckoutErrorMessage('Failed to update shipping method.');
          } finally {
            dispatch(setApiLoading(false));
          }
        },
        [setApiLoading, setCartData, cartData, storeData],
      );

      useEffect(() => {
        if (availableShippingMethods?.length) {
          const initialShippingMethod =
            availableShippingMethods.find(
              method => method.ID === cartData?.ShippingMethodID,
            ) || availableShippingMethods[0];
          dispatch(setSelectedShippingMethod(initialShippingMethod));
        }
      }, [dispatch, availableShippingMethods]);

      const handleShippingMethodChange = useCallback(
        (shippingMethod: V2ShippingMethod) => {
          dispatch(setSelectedShippingMethod(shippingMethod));
          setShippingMethod(shippingMethod);
        },
        [dispatch],
      );
      const isEditPage = getIsEdit();
      if (!isEditPage) {
        if (!cartData?.ShippingRequired && cartData?.LineItems) {
          return <></>;
        }

        if (
          !!cartData?.ShippingRequired &&
          (!availableShippingMethods || availableShippingMethods?.length === 0)
        ) {
          return <></>;
        }
      }

      return (
        <>
          {!displayAfterShippingFormIsFilled?.isDisplayAfterShippingFormIsFilled ||
          isValidShippingForm ? (
            showComponent?.includes(currentDeviceType) && (
              <div
                style={{
                  ...marginStyles,
                  ...getPaddingStyles(padding, currentDeviceType),
                  ...getBorderProps(border, currentDeviceType),
                }}
                className={`flex-col justify-between ${border?.borderStyle?.[currentDeviceType]} text-editor-content`}>
                <div
                  className={`relative mt-4 grid gap-x-4 grid-cols-1 ${isApiLoading ? 'opacity-20' : 'opacity-100'}`}>
                  {
                    <fieldset aria-label="shipping methods">
                      <RadioGroup
                        value={selectedShippingMethod}
                        onChange={e => {
                          if (e) {
                            handleShippingMethodChange(e);
                          }
                        }}
                        className="-space-y-px rounded-md bg-white">
                        {availableShippingMethods?.map(
                          (
                            shippingMethod: ShippingMethod,
                            idx: number,
                            arr: ShippingMethod[],
                          ) => (
                            <RadioGroup.Option
                              key={shippingMethod.ID}
                              value={shippingMethod}
                              aria-label={shippingMethod.Name}
                              aria-description={shippingMethod.Description}
                              style={{
                                backgroundColor: background?.isInherit
                                  ? 'inherit'
                                  : background?.color,
                              }}>
                              {({ checked }) => (
                                <div
                                  className={`${idx === 0 ? 'rounded-tl-md rounded-tr-md' : ''}  ${
                                    idx === arr.length - 1
                                      ? 'rounded-bl-md rounded-br-md'
                                      : ''
                                  }
                              ${checked ? '' : 'border-gray-200 '}
                              'relative flex cursor-pointer border p-4 focus:outline-none'
                              `}
                                  style={
                                    checked
                                      ? {
                                          backgroundColor:
                                            selectedOptionCustomization
                                              ?.background?.isInherit
                                              ? 'inherit'
                                              : selectedOptionCustomization
                                                  ?.background?.color,
                                          borderColor:
                                            selectedOptionCustomization?.borderColor,
                                          position: 'relative',
                                        }
                                      : {}
                                  }>
                                  <>
                                    <span
                                      style={{
                                        backgroundColor: checked
                                          ? radioColor
                                          : 'white',
                                        width: radioSize + 'px',
                                        height: radioSize + 'px',
                                      }}
                                      className={classNames(
                                        checked
                                          ? 'border-transparent'
                                          : 'border-gray-300 bg-white',
                                        'mt-0.5 flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-full border',
                                      )}
                                      aria-hidden="true">
                                      <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                    </span>
                                    <div
                                      className={`flex justify-between flex-grow ml-3`}>
                                      <span className="flex flex-col justify-start w-full">
                                        <span
                                          style={{
                                            color:
                                              newTitle?.textColor?.['default'],
                                            fontFamily:
                                              newTitle?.fontFamily !==
                                              'inherit-global'
                                                ? newTitle?.fontFamily
                                                : 'inherit',
                                            lineHeight: newTitle?.lineHeight,
                                            letterSpacing:
                                              newTitle?.letterSpace + 'px',
                                          }}
                                          className={` block w-full ${newTitle.fontSize?.['default']} ${newTitle.weight?.['default']} ${newTitle.textAlign?.['default']}`}>
                                          {shippingMethod?.Name}
                                        </span>
                                        <span
                                          style={{
                                            color:
                                              newDescription?.textColor?.[
                                                'default'
                                              ],
                                            fontFamily:
                                              newDescription?.fontFamily !==
                                              'inherit-global'
                                                ? newDescription?.fontFamily
                                                : 'inherit',
                                            lineHeight:
                                              newDescription?.lineHeight,
                                            letterSpacing:
                                              newDescription?.letterSpace +
                                              'px',
                                          }}
                                          className={` block w-full ${newDescription.fontSize?.['default']} ${newDescription.weight?.['default']} ${newDescription.textAlign?.['default']}`}>
                                          {shippingMethod?.Description}
                                        </span>
                                      </span>
                                      <span className="flex flex-col items-start">
                                        <RadioGroup.Description
                                          as="span"
                                          style={{
                                            color:
                                              newPrice?.textColor?.['default'],
                                            fontFamily:
                                              newPrice?.fontFamily !==
                                              'inherit-global'
                                                ? newPrice?.fontFamily
                                                : 'inherit',
                                            lineHeight: newPrice?.lineHeight,
                                            letterSpacing:
                                              newPrice?.letterSpace + 'px',
                                          }}
                                          className={`${newPrice.fontSize?.['default']} ${newPrice.weight?.['default']}`}>
                                          {+shippingMethod?.Price == 0 &&
                                          isShowFreeForZero
                                            ? 'Free'
                                            : `${getCurrencyWithSymbol(
                                                cartData?.currencyCode || 'USD',
                                                shippingMethod?.Price,
                                              )}`}
                                        </RadioGroup.Description>
                                        {shippingMethod?.DiscountedPrice &&
                                          shippingMethod?.DiscountedPrice !==
                                            '0.00' &&
                                          parseFloat(shippingMethod?.Price) !==
                                            parseFloat(
                                              shippingMethod?.DiscountedPrice,
                                            ) && (
                                            <RadioGroup.Description
                                              as="span"
                                              style={{
                                                color:
                                                  price?.textColor?.['default'],
                                              }}
                                              className="text-sm font-medium line-through">
                                              {getCurrencyWithSymbol(
                                                cartData?.currencyCode || 'USD',
                                                shippingMethod?.Price,
                                              )}
                                            </RadioGroup.Description>
                                          )}
                                      </span>
                                    </div>
                                  </>
                                </div>
                              )}
                            </RadioGroup.Option>
                          ),
                        )}
                      </RadioGroup>
                    </fieldset>
                  }
                  {isApiCartLoading ? (
                    <ShippingMethodLoader />
                  ) : isApiLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  ) : null}
                </div>
              </div>
            )
          ) : (
            <div
              style={{
                ...marginStylesForLoading,
                ...getPaddingStyles(
                  displayAfterShippingFormIsFilled?.padding,
                  currentDeviceType,
                ),
                ...getBorderProps(
                  displayAfterShippingFormIsFilled?.border,
                  currentDeviceType,
                ),
                backgroundColor: displayAfterShippingFormIsFilled?.background
                  ?.isInherit
                  ? 'inherit'
                  : displayAfterShippingFormIsFilled?.background?.color,
              }}
              className={`${displayAfterShippingFormIsFilled?.border?.borderStyle?.[currentDeviceType]} w-full text-editor-content`}
              dangerouslySetInnerHTML={{
                __html: getQuillEditorText(
                  displayAfterShippingFormIsFilled?.text,
                  state,
                ),
              }}></div>
          )}
        </>
      );
    },
  };
};
